import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, ShoppingCart, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';

const Header = () => {
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalog?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const cartCount = getCartCount();

  return (
    <>
      {/* Top banner */}
      <div className="bg-gray-100 text-center py-2 px-4 text-sm">
        <p>Welcome to our store</p>
      </div>

      {/* Main header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/edible-greetings" className="text-xl font-bold text-purple-600 hover:text-purple-700 transition-colors">
              Edible Greetings
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/edible-greetings" className="text-gray-900 hover:text-gray-600 transition-colors">
                Home
              </Link>
              <Link to="/edible-greetings/catalog" className="text-gray-900 hover:text-gray-600 transition-colors">
                Catalog
              </Link>
              <Link to="/contact" className="text-gray-900 hover:text-gray-600 transition-colors">
                Contact
              </Link>
            </nav>

            {/* Icons */}
            <div className="flex items-center space-x-4">
              {/* Search */}
              {searchOpen ? (
                <form onSubmit={handleSearch} className="hidden md:flex items-center">
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-48 h-9"
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setSearchOpen(false)}
                    className="ml-1"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </form>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="hidden md:block text-gray-900 hover:text-gray-600 transition-colors"
                  aria-label="Search"
                >
                  <Search className="h-5 w-5" />
                </button>
              )}

              {/* Account */}
              <Link
                to="/account"
                className="text-gray-900 hover:text-gray-600 transition-colors"
                aria-label="Account"
              >
                <User className="h-5 w-5" />
              </Link>

              {/* Cart */}
              <Link
                to="/edible-greetings/cart"
                className="relative text-gray-900 hover:text-gray-600 transition-colors"
                aria-label="Shopping Cart"
              >
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <button className="md:hidden text-gray-900" aria-label="Menu">
                    <Menu className="h-6 w-6" />
                  </button>
                </SheetTrigger>
                <SheetContent>
                  <nav className="flex flex-col space-y-4 mt-8">
                    <Link
                      to="/edible-greetings"
                      className="text-lg text-gray-900 hover:text-gray-600 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Home
                    </Link>
                    <Link
                      to="/edible-greetings/catalog"
                      className="text-lg text-gray-900 hover:text-gray-600 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Catalog
                    </Link>
                    <Link
                      to="/contact"
                      className="text-lg text-gray-900 hover:text-gray-600 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Contact
                    </Link>
                    <form onSubmit={handleSearch} className="pt-4">
                      <Input
                        type="text"
                        placeholder="Search products..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </form>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
